import React, { useContext, useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { OrderContext } from '../App';
import 'leaflet/dist/leaflet.css';
import './MapComponent.css';
import { reverseGeocode } from '../services/api';

// Fix default Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

/**
 * MapComponent - Displays interactive map with pickup/delivery markers and route
 * 
 * Props:
 * - originAddress: Address string for pickup location
 * - destinationAddress: Address string for delivery location  
 * - pickupCoords: [lat, lon] array for pickup (optional - will geocode if not provided)
 * - deliveryCoords: [lat, lon] array for delivery (optional - will geocode if not provided)
 * - routeCoords: Array of [lat, lon] from backend route_window (preferred)
 * - routePhase: not_dispatched | to_pickup | loaded | delivered
 * - title: Map title to display
 * - selectedOrder: Order object with truck/date info for route fetching
 */
export default function MapComponent({ 
  originAddress,
  destinationAddress,
  pickupCoords: pickupCoordsProp = null,
  deliveryCoords: deliveryCoordsProp = null,
  routeCoords: routeCoordsProp = null,
  routePhase: routePhaseProp = null,
  title = "Order Location",
  selectedOrder: selectedOrderProp = null,
  disableLiveRouteFetch = false,
  currentLocationLabel = null
}) {
  
  // =============================================================================
  // STATE & CONTEXT SETUP
  // =============================================================================
  
  const {
    selectedOrder: contextSelectedOrder,
    instance,
    setCoordinates,
    routeCoordinates: contextRouteCoordinates,
    routeWindow: contextRouteWindow
  } = useContext(OrderContext) || {};
  const selectedOrder = selectedOrderProp || contextSelectedOrder;
  const completedStatuses = ['DELVD','COMPLETE','COMPLETED','ACC','APPRVD','BILLD','PRINTED','AUDIT'];
  const routePhase = routePhaseProp || contextRouteWindow?.phase || null;
  
  // Map state
  const [pickupCoords, setPickupCoords] = useState(pickupCoordsProp || null);
  const [deliveryCoords, setDeliveryCoords] = useState(deliveryCoordsProp || null);
  const [routeCoords, setRouteCoords] = useState(routeCoordsProp || []);
  const [loading, setLoading] = useState(true);
  const [isLiveRoute, setIsLiveRoute] = useState(false);
  // Track previously displayed order to reset route when switching orders
  const prevOrderIdRef = useRef(null);
  const mapRef = useRef(null);

  // Constants
  const defaultCenter = [43.6532, -79.3832]; // Toronto area
  const originAddr = originAddress || selectedOrder?.origin_address;
  const destAddr = destinationAddress || selectedOrder?.destination_address;

  // =============================================================================
  // COORDINATE MANAGEMENT
  // =============================================================================
  
  /**
   * Initialize coordinates from props when component mounts or props change
   */
  useEffect(() => {
    if (pickupCoordsProp) {
      setPickupCoords(pickupCoordsProp);
      console.log('MapComponent: Pickup coordinates set from props:', pickupCoordsProp);
    }
    if (deliveryCoordsProp) {
      setDeliveryCoords(deliveryCoordsProp);
      console.log('MapComponent: Delivery coordinates set from props:', deliveryCoordsProp);
    }
    if (routeCoordsProp && routeCoordsProp.length > 0) {
      setRouteCoords(routeCoordsProp);
      setIsLiveRoute(true);
      console.log('MapComponent: Route coordinates set from props:', routeCoordsProp.length, 'points');
    }
  }, [pickupCoordsProp, deliveryCoordsProp, routeCoordsProp]);

  /**
   * Portal: apply backend routeCoordinates from OrderExtraInfo via context.
   */
  useEffect(() => {
    if (disableLiveRouteFetch) return;
    if (routeCoordsProp && routeCoordsProp.length > 0) return;
    if (!contextRouteCoordinates) return;
    if (contextRouteCoordinates.length > 0) {
      setRouteCoords(contextRouteCoordinates);
      setIsLiveRoute(true);
    } else if (routePhase && routePhase !== 'not_dispatched') {
      // Backend decided phase but no GPS points — clear any old trail.
      setRouteCoords([]);
      setIsLiveRoute(false);
    }
  }, [contextRouteCoordinates, routeCoordsProp, disableLiveRouteFetch, routePhase]);

  /**
   * Reset coordinates when addresses change (only if not provided via props)
   */
  useEffect(() => {
    if (pickupCoordsProp || deliveryCoordsProp || routeCoordsProp) return;
    
    console.log('MapComponent: Resetting coordinates due to address change');
    setPickupCoords(null);
    setDeliveryCoords(null);
    setRouteCoords([]);
  }, [originAddr, destAddr, pickupCoordsProp, deliveryCoordsProp, routeCoordsProp]);

  // =============================================================================
  // GEOCODING (when coordinates not provided via props)
  // =============================================================================
  
  useEffect(() => {
    // Skip geocoding if coordinates already provided
    if (pickupCoordsProp && deliveryCoordsProp) {
      console.log('MapComponent: Coordinates provided via props, skipping geocoding');
      setLoading(false);
      return;
    }

    if (!originAddr && !destAddr) {
      console.log('MapComponent: No addresses provided, skipping geocoding');
      setLoading(false);
      return;
    }

    console.log('MapComponent: Starting geocoding for addresses');
    setLoading(true);
    
    const geocodeAddresses = async () => {
      try {
        const tasks = [];
        let pickup = pickupCoordsProp;
        let delivery = deliveryCoordsProp;

        // Geocode pickup address if not provided
        if (!pickup && originAddr) {
          tasks.push(
            reverseGeocode(originAddr, instance)
              .then(loc => {
                if (loc) {
                  pickup = [loc.lat, loc.lon];
                  setPickupCoords(pickup);
                }
              })
          );
        }

        // Geocode delivery address if not provided
        if (!delivery && destAddr) {
          tasks.push(
            reverseGeocode(destAddr, instance)
              .then(loc => {
                if (loc) {
                  delivery = [loc.lat, loc.lon];
                  setDeliveryCoords(delivery);
                }
              })
          );
        }

        await Promise.all(tasks);
        
        // Update parent context with coordinates
        if (setCoordinates) {
          setCoordinates({ pickup, delivery });
        }
      } catch (err) {
        console.error('MapComponent: Geocoding error:', err);
      } finally {
        setLoading(false);
      }
    };

    geocodeAddresses();
  }, [originAddr, destAddr, instance, setCoordinates, pickupCoordsProp, deliveryCoordsProp]);

  // =============================================================================
  // ROUTE MANAGEMENT (backend-owned; FE only draws)
  // =============================================================================

  /**
   * Fake curve only when phase is not_dispatched (pins only otherwise if no GPS).
   */
  useEffect(() => {
    if (routeCoords.length > 0 || !pickupCoords || !deliveryCoords) return;
    if (routePhase && routePhase !== 'not_dispatched') return;
    // Wait for portal details before drawing a curve for an active order.
    if (!disableLiveRouteFetch && selectedOrder?.order_id && !routePhase) return;

    const generateCurvePoints = (start, end) => {
      const midLat = (start[0] + end[0]) / 2;
      const midLon = (start[1] + end[1]) / 2;
      const dLat = end[0] - start[0];
      const dLon = end[1] - start[1];
      const controlPoint = [midLat + 0.2 * dLon, midLon - 0.2 * dLat];
      const points = [];
      for (let i = 0; i <= 100; i++) {
        const t = i / 100;
        const lat = (1 - t) ** 2 * start[0] + 2 * (1 - t) * t * controlPoint[0] + t ** 2 * end[0];
        const lon = (1 - t) ** 2 * start[1] + 2 * (1 - t) * t * controlPoint[1] + t ** 2 * end[1];
        points.push([lat, lon]);
      }
      return points;
    };

    setRouteCoords(generateCurvePoints(pickupCoords, deliveryCoords));
    setIsLiveRoute(false);
  }, [pickupCoords, deliveryCoords, routeCoords.length, routePhase, disableLiveRouteFetch, selectedOrder?.order_id]);

  // =============================================================================
  // MAP DISPLAY LOGIC
  // =============================================================================
  
  /**
   * Calculate map center based on available coordinates
   */
  const mapCenter = pickupCoords && deliveryCoords
    ? [(pickupCoords[0] + deliveryCoords[0]) / 2, (pickupCoords[1] + deliveryCoords[1]) / 2]
    : pickupCoords || deliveryCoords || defaultCenter;

  /**
   * Auto-fit map bounds when route is available
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !routeCoords.length) return;
    
    try {
      const bounds = L.latLngBounds(routeCoords);
      
      // Include markers in bounds
      if (pickupCoords) bounds.extend(pickupCoords);
      if (deliveryCoords) bounds.extend(deliveryCoords);
      
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 15,
        animate: true
      });
    } catch (err) {
      console.error('MapComponent: Error fitting bounds:', err);
    }
  }, [routeCoords, pickupCoords, deliveryCoords]);

  // =============================================================================
  // MAP RECENTER / FIT WHEN ONLY MARKERS AVAILABLE (no route)
  // =============================================================================

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // If we already have a route rendered, the existing fitBounds effect will
    // handle viewport updates. Only handle the scenario where no route is
    // available and at least one marker exists.
    if (routeCoords.length > 0) return;

    if (pickupCoords && deliveryCoords) {
      // Fit bounds to both pickup and delivery markers
      const bounds = L.latLngBounds([pickupCoords, deliveryCoords]);
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 15,
        animate: true
      });
    } else if (pickupCoords || deliveryCoords) {
      // Only one marker available – center the map on it
      const center = pickupCoords || deliveryCoords;
      map.setView(center, 12, { animate: true });
    }
  }, [pickupCoords, deliveryCoords, routeCoords.length]);

  // =============================================================================
  // MARKER ICONS
  // =============================================================================
  
  const pickupIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    shadowSize: [41, 41]
  });
  
  const dropoffIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    shadowSize: [41, 41]
  });

  const truckIcon = new L.Icon({
    // In-transit truck icon
    iconUrl: 'https://cdn-icons-png.flaticon.com/128/2203/2203124.png',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -12],
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    shadowSize: [41, 41]
  });

  const truckCompleteIcon = new L.Icon({
    // Completed delivery icon
    iconUrl: 'https://cdn-icons-png.flaticon.com/128/411/411712.png',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -12],
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    shadowSize: [41, 41]
  });

  // =============================================================================
  // ROUTE RESET ON ORDER CHANGE
  // =============================================================================

  useEffect(() => {
    const currentOrderId = selectedOrder?.order_id;

    // Only trigger when the order itself changes
    if (!currentOrderId || currentOrderId === prevOrderIdRef.current) return;

    // Update reference so we don't re-enter for the same order
    prevOrderIdRef.current = currentOrderId;

    // If a fresh set of route coordinates has been provided via props, keep them.
    // Otherwise clear so the component can apply backend points or a curve.
    if (!routeCoordsProp || routeCoordsProp.length === 0) {
      setRouteCoords([]);
      setIsLiveRoute(false);
    }
  }, [selectedOrder?.order_id, routeCoordsProp]);

  const liveBadgeSuffix = routePhase === 'to_pickup'
    ? (currentLocationLabel ? ` • heading to pickup • ${currentLocationLabel}` : ' • heading to pickup')
    : (currentLocationLabel ? ` • ${currentLocationLabel}` : '');

  // =============================================================================
  // RENDER
  // =============================================================================
  
  if (loading) {
    return (
      <div className="map-container">
        <h2>{title}</h2>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 600,
          bgcolor: 'rgba(255,255,255,0.7)'
        }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className="map-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        {isLiveRoute && (
          <span style={{ fontSize: 12, color: '#0a7a0a', fontWeight: 600 }}>
            Live (GPS){liveBadgeSuffix}
          </span>
        )}
        {!isLiveRoute && routeCoords.length > 0 && (
          <span style={{ fontSize: 12, color: '#a37c00', fontWeight: 600 }}>Visualized route</span>
        )}
      </div>
      
      <MapContainer
        key={(selectedOrder?.order_id) || `${originAddr}-${destAddr}` || 'static'}
        center={mapCenter}
        zoom={8}
        style={{ height: 600, width: '100%' }}
        whenCreated={mapInstance => { mapRef.current = mapInstance; }}
        ref={mapRef}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />

        {/* Pickup Marker */}
        {pickupCoords && (
          <Marker position={pickupCoords} icon={pickupIcon}>
            <Popup><strong>Pickup:</strong><br />{originAddr}</Popup>
          </Marker>
        )}

        {/* Delivery Marker */}
        {deliveryCoords && (
          <Marker position={deliveryCoords} icon={dropoffIcon}>
            <Popup><strong>Delivery:</strong><br />{destAddr}</Popup>
          </Marker>
        )}

        {/* Route Line */}
        {routeCoords.length > 0 && (
          <Polyline
            positions={routeCoords}
            color="black"
            weight={8}
            opacity={0.8}
          />
        )}

        {/* Truck marker at last known route point */}
        {routeCoords.length > 0 && (
          <Marker
            position={routeCoords[routeCoords.length - 1]}
            icon={completedStatuses.includes(selectedOrder?.Status) ? truckCompleteIcon : truckIcon}
          >
            <Popup>
              <strong>Truck</strong><br />
              {currentLocationLabel ? currentLocationLabel : 'Last reported position'}
            </Popup>
          </Marker>
        )}

      </MapContainer>

      {/* No Data Message */}
      {!originAddr && !destAddr && (
        <p>No addresses provided. Select an order or pass origin/destination props.</p>
      )}
    </div>
  );
}